import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mic, MicOff, Send, Languages, Sparkles, PhoneOff, Volume2, Star } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { api, useAuth } from "../api";
import { gradeProfile } from "../gradeProfile";

const speak = (text, voice, onStart, onEnd, onBeat) => {
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const en = speechSynthesis.getVoices().filter((v) => v.lang?.startsWith("en"));
    // fixed voice per character (never changes between replies)
    if (en.length) u.voice = en[(voice?.vi ?? 0) % en.length];
    u.lang = "en-US";
    u.pitch = voice?.pitch ?? 1;
    u.rate = voice?.rate ?? 1;
    u.onstart = onStart; u.onend = onEnd; u.onerror = onEnd;
    u.onboundary = () => onBeat?.();
    speechSynthesis.speak(u);
  } catch { onEnd?.(); }
};

// real natural voice (OpenAI TTS from the backend); falls back to the browser voice
const playReal = (url, audioRef, onStart, onEnd, onBeat) => {
  try {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
    audioRef.current = a;
    let timer = null;
    a.onplay = () => { onStart?.(); timer = setInterval(() => onBeat?.(), 220); };
    const stop = () => { clearInterval(timer); onEnd?.(); };
    a.onended = stop; a.onerror = stop; a.onpause = stop;
    a.play().catch(stop);
    return true;
  } catch { return false; }
};

export default function Chat() {
  const { user } = useAuth();
  const profile = gradeProfile(user?.grade);
  const [chars, setChars] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [talking, setTalking] = useState(false);
  const [beat, setBeat] = useState(0);
  const [blink, setBlink] = useState(false);
  const [listening, setListening] = useState(false);
  const [showAr, setShowAr] = useState(true);
  const [points, setPoints] = useState(0);
  const [features, setFeatures] = useState({ video_chat_enabled: false, left: 0, daily: null });
  const [micLang, setMicLang] = useState("en-US");
  const endRef = useRef(null);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const lastAudio = useRef(null);
  const speechOk = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => { api.get("/chat/characters").then((r) => setChars(r.data)); }, []);
  useEffect(() => { api.get("/chat/features").then((r) => setFeatures(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => () => { try { speechSynthesis.cancel(); } catch { /* noop */ } }, []);

  // life-like idle: blink every few seconds
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 160);
    }, 3200 + Math.random() * 2200);
    return () => clearInterval(id);
  }, [active]);

  const open = async (c) => {
    setActive(c); setMessages([]); setPoints(0);
    const { data } = await api.get("/chat/history", { params: { character: c.id } });
    setMessages(data.messages);
    const hi = `Hi! I am ${c.name}. What is your name?`;
    if (!data.messages.length) {
      setMessages([{ role: "character", text: hi, translation_ar: `مرحباً! أنا ${c.name_ar}. ما اسمك؟` }]);
    }
    const line = data.messages.length ? "Welcome back! Let's talk again." : hi;
    setTimeout(async () => {
      try {
        const { data: v } = await api.post("/tts/say", { text: line, character: c.id });
        lastAudio.current = v.audio_url;
        if (playReal(v.audio_url, audioRef, () => setTalking(true),
          () => setTalking(false), () => setBeat((b) => b + 1))) return;
      } catch { /* fall back to the browser voice */ }
      speak(line, c.voice, () => setTalking(true), () => setTalking(false),
        () => setBeat((b) => b + 1));
    }, 600);
  };

  const send = async (msg) => {
    const clean = (msg || "").trim();
    if (!clean) return;
    setText("");
    setMessages((m) => [...m, { role: "student", text: clean }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/send", { character: active.id, message: clean });
      setMessages((m) => [...m, {
        role: "character", text: data.reply, translation_ar: data.translation_ar,
        correction: data.correction, correction_ar: data.correction_ar,
      }]);
      setPoints((p) => p + (data.points_earned || 0));
      if (features.daily) {
        setFeatures((f) => ({ ...f, daily: { ...f.daily, left: Math.max(0, f.daily.left - 1) } }));
      }
      lastAudio.current = data.audio_url || null;
      const started = data.audio_url && playReal(data.audio_url, audioRef,
        () => setTalking(true), () => setTalking(false), () => setBeat((b) => b + 1));
      if (!started) {
        speak(data.reply, data.voice || active.voice, () => setTalking(true),
          () => setTalking(false), () => setBeat((b) => b + 1));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر الحصول على رد");
    } finally { setBusy(false); }
  };

  const listen = () => {
    if (!speechOk) return toast.error("المتصفح لا يدعم التعرّف الصوتي — اكتب رسالتك");
    const R = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new R();
    recRef.current = r;
    r.lang = micLang; r.interimResults = false;
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onerror = () => { setListening(false); toast.error("لم أسمعك جيداً، حاول مرة أخرى"); };
    r.onresult = (e) => send(e.results[0][0].transcript);
    r.start();
  };

  if (!active) {
    return (
      <StudentShell>
        <h1 className="text-4xl font-black text-white glow-text">حوار الذكاء الاصطناعي</h1>
        <p className="text-white/70 mt-2 text-sm">
          اختر شخصية وابدأ المكالمة — تحدّث معها بالعربي أو بالإنجليزي، وترد عليك بالإنجليزية بصوتها،
          تصحّح أخطاءك، وتكسب نقاطاً
          ({profile.label.ar}).</p>
        {features.daily?.cap > 0 && (
          <p className="text-xs mt-2" style={{ color: "#ffb43f" }} data-testid="chat-daily-left">
            محادثاتك المتبقية اليوم: {features.daily.left} من {features.daily.cap}</p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8" data-testid="character-grid">
          {chars.map((c, i) => (
            <motion.button key={c.id} data-testid={`character-${c.id}`} onClick={() => open(c)}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(i, 4) * 0.05 }}
              whileHover={{ y: -6 }}
              className="play-card overflow-hidden p-0">
              <motion.img src={c.avatar} alt={c.name} className="w-full aspect-square object-cover"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 5 + i * 0.4, repeat: Infinity, ease: "easeInOut" }} />
              <div className="p-3">
                <div className="font-black text-sm">{c.name_ar}</div>
                <div className="text-xs text-white/55" dir="ltr">{c.emoji} {c.name}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </StudentShell>
    );
  }

  const last = [...messages].reverse().find((m) => m.role === "character");

  return (
    <StudentShell>
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6" data-testid="call-screen">
        {/* --------- video-call stage --------- */}
        <div className="play-card relative overflow-hidden p-0" style={{ borderColor: "var(--play-primary)" }}>
          <div className="absolute top-3 start-3 z-10 flex items-center gap-2 rounded-full px-3 py-1
            text-xs font-black text-white" style={{ background: "rgba(4,12,32,.6)" }}>
            <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: "#3ec46d" }} />
            {talking ? "يتحدث الآن..." : listening ? "أسمعك..." : "متصل"}
          </div>
          <div className="absolute top-3 end-3 z-10 flex items-center gap-1 rounded-full px-3 py-1
            text-xs font-black text-[#05122e]" style={{ background: "linear-gradient(180deg,#ffd166,#ff9f1c)" }}
            data-testid="call-points"><Star size={12} />{points}</div>

          <div className="relative w-full aspect-square overflow-hidden"
            style={{ perspective: 900 }}>
            {/* body: slow breathing + weight shift */}
            <motion.div className="absolute inset-0"
              animate={talking
                ? { y: [0, -4, 0, -2, 0], rotate: [0, -0.7, 0.5, -0.3, 0], scale: [1, 1.012, 1] }
                : { y: [0, -2.5, 0], rotate: [0, 0.4, -0.4, 0], scale: [1, 1.006, 1] }}
              transition={{ duration: talking ? 3.2 : 5.6, repeat: Infinity, ease: "easeInOut" }}>
              {/* head area: gentle sway + nod synced to speech beats */}
              <motion.div className="w-full h-full"
                style={{ transformOrigin: "50% 85%" }}
                animate={talking
                  ? { rotateZ: [0, -1.6, 1.2, -0.8, 0], rotateY: [0, 3.5, -2.5, 1.5, 0], y: [0, -3, 1, -2, 0] }
                  : { rotateZ: [0, 0.8, -0.8, 0], rotateY: [0, -2, 2, 0], y: [0, -1.5, 0] }}
                transition={{ duration: talking ? 2.4 : 7, repeat: Infinity, ease: "easeInOut" }}>
                <motion.img src={active.avatar} alt={active.name} data-testid="call-avatar"
                  animate={talking
                    ? { scale: [1.03, 1.045, 1.035] }
                    : { scale: [1, 1.01, 1] }}
                  transition={{ duration: talking ? 0.5 : 6, repeat: Infinity, ease: "easeInOut" }}
                  className="w-full h-full object-cover" />
              </motion.div>
            </motion.div>

            {/* lip movement: soft shadow over the mouth area pulsing with each speech beat */}
            <motion.div className="absolute pointer-events-none" key={`lip-${beat % 3}`}
              style={{
                left: "40%", width: "20%", top: "58%", height: "10%",
                borderRadius: "50%",
                background: "radial-gradient(ellipse at center, rgba(20,6,10,.55), rgba(20,6,10,0) 70%)",
                filter: "blur(3px)",
              }}
              animate={talking
                ? { scaleY: [0.35, 1.15, 0.5, 1, 0.4], scaleX: [0.9, 1.05, 0.95, 1.08, 0.9], opacity: 0.75 }
                : { scaleY: 0.25, opacity: 0 }}
              transition={{ duration: 0.46, repeat: talking ? Infinity : 0, ease: "easeInOut" }} />

            {/* gesturing hand shadow near the lower edge, only while speaking */}
            <motion.div className="absolute pointer-events-none"
              style={{
                left: "8%", bottom: "16%", width: "26%", height: "26%",
                background: "radial-gradient(circle at 40% 40%, rgba(4,12,32,.35), transparent 68%)",
                filter: "blur(6px)",
              }}
              animate={talking
                ? { x: [0, 14, -6, 10, 0], y: [0, -12, -4, -9, 0], rotate: [0, -9, 6, -4, 0], opacity: 0.9 }
                : { opacity: 0 }}
              transition={{ duration: 2.1, repeat: talking ? Infinity : 0, ease: "easeInOut" }} />

            {/* blink flicker + speaking glow make the character feel alive */}
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-150"
              style={{ background: "rgba(4,12,32,.55)", opacity: blink ? 0.28 : 0 }} />
            <motion.div className="absolute inset-0 pointer-events-none"
              animate={{ opacity: talking ? [0.12, 0.26, 0.14] : 0 }}
              transition={{ duration: 0.9, repeat: talking ? Infinity : 0 }}
              style={{
                background: "radial-gradient(circle at 50% 78%, rgba(63,169,255,.4), transparent 60%)",
              }} />
          </div>

          {talking && (
            <div className="absolute bottom-24 inset-x-0 flex justify-center gap-1" data-testid="voice-wave">
              {[0, 1, 2, 3, 4, 5, 6].map((k) => (
                <motion.span key={k} className="w-1.5 rounded-full bg-white/90"
                  animate={{ height: [6, 22, 10, 26, 8] }}
                  transition={{ duration: 0.7, repeat: Infinity, delay: k * 0.07 }} />
              ))}
            </div>
          )}

          <div className="absolute bottom-0 inset-x-0 p-4 flex items-center justify-center gap-3"
            style={{ background: "linear-gradient(0deg,rgba(4,12,32,.9),transparent)" }}>
            <button onClick={listen} disabled={busy || listening} data-testid="mic-btn"
              className="h-14 w-14 rounded-full grid place-items-center text-white disabled:opacity-60"
              style={{ background: listening ? "#e8384f" : "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
              {listening ? <MicOff /> : <Mic />}</button>
            <button onClick={() => {
              if (lastAudio.current && playReal(lastAudio.current, audioRef,
                () => setTalking(true), () => setTalking(false), () => setBeat((b) => b + 1))) return;
              if (last) {
                speak(last.text, active.voice, () => setTalking(true),
                  () => setTalking(false), () => setBeat((b) => b + 1));
              }
            }}
              disabled={!last}
              data-testid="replay-btn"
              className="h-12 w-12 rounded-full grid place-items-center text-white bg-white/15 disabled:opacity-40">
              <Volume2 size={18} /></button>
            <button onClick={() => {
              speechSynthesis.cancel();
              if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
              setActive(null);
            }} data-testid="hangup-btn"
              className="h-12 w-12 rounded-full grid place-items-center text-white"
              style={{ background: "#e8384f" }}><PhoneOff size={18} /></button>
          </div>

          {features.video_chat_enabled && (
            <div className="absolute bottom-20 inset-x-0 text-center text-[11px] text-white/70"
              data-testid="video-quota-note">
              🎬 الفيديو الناطق مفعّل لمدرستك — المتبقي هذا الأسبوع: {features.left}
            </div>
          )}
        </div>

        {/* --------- transcript --------- */}
        <div>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <h1 className="text-2xl font-black">{active.name_ar}</h1>
              <div className="text-xs text-white/55" dir="ltr">{active.name}</div>
            </div>
            <button onClick={() => setShowAr((v) => !v)} data-testid="toggle-translation-btn"
              className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
              <Languages size={14} />{showAr ? "إخفاء الترجمة" : "إظهار الترجمة"}</button>
            <button onClick={() => setMicLang((l) => (l === "en-US" ? "ar-SA" : "en-US"))}
              data-testid="mic-lang-btn" title="لغة الميكروفون"
              className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
              🎤 {micLang === "en-US" ? "English" : "عربي"}</button>
          </div>

          <div className="play-card mt-4 max-h-[45vh] overflow-y-auto space-y-4" data-testid="chat-thread">
            {!messages.length && (
              <p className="text-white/55 text-sm text-center py-6" data-testid="chat-empty">
                تحدّث أو اكتب بالعربي أو بالإنجليزي — الشخصية تفهمك وترد عليك بالإنجليزية.
                غيّر لغة الميكروفون من زر 🎤 بالأعلى</p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "student" ? "text-end" : "text-start"}
                data-testid={`chat-msg-${i}`}>
                <div className="inline-block max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed"
                  style={{
                    background: m.role === "student" ? "#ffb43f" : "rgba(255,255,255,.10)",
                    color: m.role === "student" ? "#05122e" : "#fff",
                  }} dir={/[\u0600-\u06FF]/.test(m.text || "") ? "rtl" : "ltr"}>{m.text}</div>
                {m.role === "character" && showAr && m.translation_ar && (
                  <div className="text-xs text-white/60 mt-1">{m.translation_ar}</div>
                )}
                {m.role === "character" && m.correction && (
                  <div className="mt-2 inline-flex flex-col items-start rounded-xl px-3 py-2 text-xs"
                    style={{ background: "rgba(62,196,109,.16)" }} data-testid={`chat-correction-${i}`}>
                    <span className="font-bold flex items-center gap-1"><Sparkles size={12} />
                      <span dir="ltr">{m.correction}</span></span>
                    {m.correction_ar && <span className="text-white/70 mt-1">{m.correction_ar}</span>}
                  </div>
                )}
              </div>
            ))}
            {busy && <p className="text-white/50 text-sm" data-testid="chat-typing">...يفكّر</p>}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2 mt-4">
            <input className="input flex-1" dir="auto" value={text} data-testid="chat-input"
              onChange={(e) => setText(e.target.value)}
              placeholder="اكتب بالعربي أو بالإنجليزي — سيرد عليك بالإنجليزية" />
            <button disabled={busy} data-testid="chat-send-btn"
              className="play-btn text-white flex items-center gap-1 disabled:opacity-50"
              style={{ background: "var(--play-primary)" }}><Send size={16} /></button>
          </form>
        </div>
      </div>
    </StudentShell>
  );
}
